import { useTranslation } from 'react-i18next';
import type { Reference } from '@peruselab/api-client/discussions';
import { Icon } from '../../components/Icon';
import { formatAuthors, referenceLabels } from './references';

/**
 * ADR 0016 D19–D22 (#910): one numbered citation -- authors, year, title (linked), venue -- and
 * its labels: "Retracted", "Preprint, not peer-reviewed", "Web source", "PeruseLab data". An
 * accepted, sound reference carries no label.
 */
export function ReferenceCitation({ reference, number }: { readonly reference: Reference; readonly number: number }) {
  const { t } = useTranslation();
  const { names, more } = formatAuthors(reference.authors);
  const title = reference.title ?? reference.identifier;
  const href = reference.standing === 'PERUSELAB' ? reference.identifier : reference.url;
  return (
    <span className="reference-citation">
      <span className="reference-number">[{number}]</span>{' '}
      {names ? (
        <span dir="auto">
          {names}
          {more > 0 ? ` ${t('discussions.references.moreAuthors', { count: more })}` : ''}
          {reference.year ? ` (${reference.year})` : ''}.{' '}
        </span>
      ) : reference.year ? (
        <span>({reference.year}) </span>
      ) : null}
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="reference-title" dir="auto">
          {title}
          <Icon name="openInNew" size="0.8rem" />
        </a>
      ) : (
        <span className="reference-title" dir="auto">
          {title}
        </span>
      )}
      {reference.venue ? (
        <span className="reference-venue" dir="auto">
          {' '}
          {reference.venue}.
        </span>
      ) : null}
      {referenceLabels(reference).map((label) => (
        <span key={label} className={`reference-label reference-label--${label}`}>
          {t(`discussions.references.labels.${label}`)}
        </span>
      ))}
    </span>
  );
}

/** A comment's citations, numbered in order. */
export function ReferenceList({ references }: { readonly references: readonly Reference[] }) {
  const { t } = useTranslation();
  if (references.length === 0) return null;
  return (
    <ol className="reference-list" aria-label={t('discussions.references.listLabel')}>
      {references.map((reference, index) => (
        <li key={reference.id}>
          <ReferenceCitation reference={reference} number={index + 1} />
        </li>
      ))}
    </ol>
  );
}
